import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FiCheckCircle, FiClock, FiPlus, FiShare2, FiTrash2, FiCalendar, FiCopy, FiCheck, FiExternalLink, FiEdit2 } from 'react-icons/fi';
import Modal from '../components/Modal';
import Button from '../components/Button';
import SearchableSelect from '../components/SearchableSelect';
import TableScrollContainer from '../components/TableScrollContainer';
import './Agenda.css';
import { formatCurrency } from '../utils/currency';
import { formatServiceName, formatServiceNameList } from '../utils/formatServiceName';
import { calculateEstimatedDuration, formatDurationText } from '../utils/duration';
import { formatDate } from '../utils/date';

function formatPhone(telefone = '') {
  const digits = String(telefone).replace(/\D/g, '').slice(0, 11);

  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function calcularHorarioFim(horaInicio, duracaoMinutos) {
  if (!horaInicio || !duracaoMinutos) return '';
  const [h, m] = String(horaInicio).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const totalMins = h * 60 + m + Number(duracaoMinutos);
  const endH = Math.floor(totalMins / 60) % 24;
  const endM = totalMins % 60;
  return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
}

function Agenda() {
  const [agendamentos, setAgendamentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [serviceSearch, setServiceSearch] = useState('');
  const [formData, setFormData] = useState({
    cliente_id: '',
    data_agendamento: new Date().toISOString().slice(0, 10),
    hora_inicio: '13:00',
    duracao_minutos: 60,
    duracao_customizada: false,
    status: 'Agendado',
    observacoes: '',
    servico_ids: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [agendamentosResponse, clientesResponse, servicosResponse] = await Promise.all([
        axios.get('/api/agendamentos'),
        axios.get('/api/clientes'),
        axios.get('/api/servicos'),
      ]);

      setAgendamentos(agendamentosResponse.data);
      setClientes(clientesResponse.data);
      setServicos(servicosResponse.data);
    } catch (error) {
      console.error('Erro ao buscar agenda:', error);
    } finally {
      setLoading(false);
    }
  };

  const servicosSelecionados = useMemo(() => {
    return servicos.filter((servico) => formData.servico_ids.includes(String(servico.id)));
  }, [formData.servico_ids, servicos]);

  const filteredServicos = useMemo(() => {
    const search = serviceSearch
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

    if (!search) {
      return servicos;
    }

    return servicos.filter((servico) => {
      const text = `${formatServiceName(servico.nome) || ''} ${servico.descricao || ''}`
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

      return text.includes(search);
    });
  }, [serviceSearch, servicos]);

  const toggleServico = (servicoId) => {
    const normalizedId = String(servicoId);

    setFormData((prev) => {
      const nextServicoIds = prev.servico_ids.includes(normalizedId)
        ? prev.servico_ids.filter((id) => id !== normalizedId)
        : [...prev.servico_ids, normalizedId];

      const nextServicos = servicos.filter((s) => nextServicoIds.includes(String(s.id)));
      const { totalMinutos } = calculateEstimatedDuration(nextServicos);

      const duracao_minutos = prev.duracao_customizada
        ? prev.duracao_minutos
        : (totalMinutos || 60);

      return {
        ...prev,
        servico_ids: nextServicoIds,
        duracao_minutos,
      };
    });
  };

  const handleOpenNew = () => {
    setEditingId(null);
    setServiceSearch('');
    setFormData({
      cliente_id: '',
      data_agendamento: new Date().toISOString().slice(0, 10),
      hora_inicio: '13:00',
      duracao_minutos: 60,
      duracao_customizada: false,
      status: 'Agendado',
      observacoes: '',
      servico_ids: [],
    });
    setShowModal(true);
  };

  const handleOpenEdit = (agendamento) => {
    setEditingId(agendamento.id);
    setServiceSearch('');

    let ids = [];
    if (Array.isArray(agendamento.servico_ids) && agendamento.servico_ids.length > 0) {
      ids = agendamento.servico_ids.map(String);
    } else if (agendamento.servicos_nomes) {
      const nomesList = String(agendamento.servicos_nomes)
        .split(/\s*[|,]\s*/)
        .map((n) => n.trim().toLowerCase());
      ids = servicos
        .filter((s) => nomesList.includes((s.nome || '').toLowerCase()))
        .map((s) => String(s.id));
    }

    const agendamentoDuracao = Number(agendamento.duracao_minutos) || 60;
    const duracaoCustomizada = Boolean(agendamento.duracao_personalizada);

    setFormData({
      cliente_id: String(agendamento.cliente_id || ''),
      data_agendamento: agendamento.data_agendamento || new Date().toISOString().slice(0, 10),
      hora_inicio: agendamento.hora_inicio || '13:00',
      duracao_minutos: agendamentoDuracao,
      duracao_customizada: duracaoCustomizada,
      status: agendamento.status || 'Agendado',
      observacoes: agendamento.observacoes || '',
      servico_ids: ids,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setServiceSearch('');
    setFormData({
      cliente_id: '',
      data_agendamento: new Date().toISOString().slice(0, 10),
      hora_inicio: '13:00',
      duracao_minutos: 60,
      duracao_customizada: false,
      status: 'Agendado',
      observacoes: '',
      servico_ids: [],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.cliente_id) {
      alert('Por favor, selecione um cliente no campo de busca.');
      return;
    }

    if (!formData.data_agendamento) {
      alert('Por favor, selecione a data do agendamento.');
      return;
    }

    if (!formData.hora_inicio) {
      alert('Por favor, informe o horário de início.');
      return;
    }

    if (!formData.servico_ids || formData.servico_ids.length === 0) {
      alert('Por favor, marque pelo menos um serviço na lista de procedimentos.');
      return;
    }

    try {
      const payload = {
        ...formData,
        cliente_id: Number(formData.cliente_id),
        servico_ids: formData.servico_ids.map((id) => Number(id)),
        duracao_minutos: Number(formData.duracao_minutos) || 60,
      };

      if (editingId) {
        await axios.put(`/api/agendamentos/${editingId}`, payload);
      } else {
        await axios.post('/api/agendamentos', payload);
      }

      await fetchData();
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      alert(error.response?.data?.error || 'Não foi possível salvar o agendamento.');
    }
  };

  const handleStatusChange = async (agendamento, newStatus) => {
    const normalizedNew = newStatus.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const normalizedCurrent = (agendamento.status || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (normalizedNew === 'concluido' && normalizedCurrent !== 'concluido') {
      await handleConcluir(agendamento.id);
      return;
    }
    try {
      await axios.put(`/api/agendamentos/${agendamento.id}`, {
        cliente_id: agendamento.cliente_id,
        data_agendamento: agendamento.data_agendamento,
        hora_inicio: agendamento.hora_inicio,
        status: newStatus,
        observacoes: agendamento.observacoes,
        servico_ids: (agendamento.servico_ids || []).map((id) => Number(id)),
      });
      await fetchData();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const handleConcluir = async (id, forcar = false) => {
    try {
      await axios.post(`/api/agendamentos/${id}/concluir`, { forcar });
      await fetchData();
      alert('Agendamento concluído e lançado em receitas.');
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.tipo === 'duplicado_manual') {
        const confirmar = window.confirm(
          'Atenção: já existe um lançamento manual de receita para este cliente nesta data.\n\nDeseja registrar mesmo assim (pode gerar duplicata)?'
        );
        if (confirmar) {
          await handleConcluir(id, true);
        }
        return;
      }
      console.error('Erro ao concluir agendamento:', error);
      alert(error.response?.data?.error || 'Não foi possível concluir o agendamento.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir este agendamento?')) return;

    try {
      await axios.delete(`/api/agendamentos/${id}`);
      await fetchData();
    } catch (error) {
      console.error('Erro ao excluir agendamento:', error);
    }
  };

  const handleShareAccess = async () => {
    const portfolioUrl = process.env.REACT_APP_PORTFOLIO_URL || 'http://localhost:5173';
    const accessUrl = `${portfolioUrl.replace(/\/$/, '')}/agendamento`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Agenda - ERP Fran Beauty',
          text: 'Acesse a agenda da Fran Beauty:',
          url: accessUrl,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(accessUrl);
        alert('Link copiado! Envie para o celular: ' + accessUrl);
        return;
      }

      alert('Link de acesso: ' + accessUrl);
    } catch (error) {
      console.error('Erro ao compartilhar link:', error);
      alert('Não foi possível compartilhar automaticamente. Use: ' + accessUrl);
    }
  };

  const handleOpenSyncModal = async () => {
    try {
      const res = await axios.get('/api/agenda/feed-info');
      setSyncInfo(res.data);
      setShowSyncModal(true);
    } catch (error) {
      console.error('Erro ao obter dados do feed de calendário:', error);
      const host = window.location.host;
      const protocol = window.location.protocol;
      setSyncInfo({
        httpUrl: `${protocol}//${host}/api/agenda/feed.ics`,
        webcalUrl: `webcal://${host}/api/agenda/feed.ics`,
        googleCalendarUrl: `https://calendar.google.com/calendar/render?cid=webcal%3A%2F%2F${encodeURIComponent(host)}%2Fapi%2Fagenda%2Ffeed.ics`,
      });
      setShowSyncModal(true);
    }
  };

  const handleCopyFeedUrl = async () => {
    if (!syncInfo?.httpUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(syncInfo.httpUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }
    } catch (err) {
      console.error('Erro ao copiar URL:', err);
    }
  };

  return (
    <div className="agenda-page">
      <div className="header-section">
        <h1>Agenda</h1>
        <div className="agenda-actions">
          <Button variant="secondary" onClick={handleOpenSyncModal} title="Sincronizar com Google Agenda e Apple Calendar">
            <FiCalendar /> Sincronizar Calendários
          </Button>
          <Button variant="secondary" onClick={handleShareAccess} title="Compartilhar link de acesso">
            <FiShare2 /> Compartilhar Link
          </Button>
          <Button
            variant="primary"
            onClick={handleOpenNew}
          >
            <FiPlus /> Novo Agendamento
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="loading">Carregando...</p>
      ) : (
        <div className="card">
          <TableScrollContainer rows={agendamentos.length} className="table-wrapper agenda-desktop-view">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Hora</th>
                  <th>Cliente</th>
                  <th>Telefone</th>
                  <th>Serviços</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {agendamentos.map((agendamento) => (
                  <tr key={agendamento.id}>
                    <td className="agenda-clickable-cell" onClick={() => handleOpenEdit(agendamento)} title="Clique para editar">
                      {formatDate(agendamento.data_agendamento)}
                    </td>
                    <td className="agenda-clickable-cell" onClick={() => handleOpenEdit(agendamento)} title="Clique para editar">
                      <strong>{agendamento.hora_inicio}</strong>
                      <div className="agenda-time-end">
                        até {calcularHorarioFim(agendamento.hora_inicio, agendamento.duracao_minutos)} ({formatDurationText(agendamento.duracao_minutos)})
                      </div>
                    </td>
                    <td className="agenda-clickable-cell" onClick={() => handleOpenEdit(agendamento)} title="Clique para editar">
                      {agendamento.cliente_nome}
                    </td>
                    <td>{formatPhone(agendamento.telefone)}</td>
                    <td>{formatServiceNameList(agendamento.servicos_nomes) || '-'}</td>
                    <td>
                      <select
                        className="agenda-status"
                        value={agendamento.status}
                        onChange={(e) => handleStatusChange(agendamento, e.target.value)}
                      >
                        <option value="Pendente">Pendente</option>
                        <option value="Agendado">Agendado</option>
                        <option value="Confirmado">Confirmado</option>
                        <option value="Concluído">Concluído</option>
                        <option value="Cancelado">Cancelado</option>
                        <option value="Faltou">Faltou</option>
                      </select>
                    </td>
                    <td className="actions-cell">
                      <div className="actions">
                        <button
                          className="edit-btn"
                          onClick={() => handleOpenEdit(agendamento)}
                          title="Editar agendamento (horário, data, procedimentos)"
                        >
                          <FiEdit2 /> <span>Editar</span>
                        </button>
                        {agendamento.status !== 'Concluído' && agendamento.status !== 'Cancelado' && (
                          <button
                            className="concluir-btn"
                            onClick={() => handleConcluir(agendamento.id)}
                            title="Concluir e lançar"
                          >
                            <FiCheckCircle />
                          </button>
                        )}
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(agendamento.id)}
                          title="Excluir"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScrollContainer>

          <div className="agenda-mobile-view">
            {agendamentos.map((agendamento) => (
              <div className="agenda-mobile-card" key={`mobile-${agendamento.id}`}>
                <div
                  className="agenda-mobile-top agenda-clickable-cell"
                  onClick={() => handleOpenEdit(agendamento)}
                  title="Clique para editar"
                >
                  <strong>{agendamento.cliente_nome}</strong>
                  <span>{formatPhone(agendamento.telefone)}</span>
                </div>

                <div
                  className="agenda-mobile-meta agenda-clickable-cell"
                  onClick={() => handleOpenEdit(agendamento)}
                  title="Clique para editar"
                >
                  <span>📅 {formatDate(agendamento.data_agendamento)}</span>
                  <span>⏰ <strong>{agendamento.hora_inicio}</strong> às {calcularHorarioFim(agendamento.hora_inicio, agendamento.duracao_minutos)} ({formatDurationText(agendamento.duracao_minutos)})</span>
                </div>

                <div className="agenda-mobile-services">
                  {formatServiceNameList(agendamento.servicos_nomes) || '-'}
                </div>

                <div className="agenda-mobile-controls">
                  <select
                    className="agenda-status"
                    value={agendamento.status}
                    onChange={(e) => handleStatusChange(agendamento, e.target.value)}
                  >
                    <option value="Pendente">Pendente</option>
                    <option value="Agendado">Agendado</option>
                    <option value="Confirmado">Confirmado</option>
                    <option value="Concluído">Concluído</option>
                    <option value="Cancelado">Cancelado</option>
                    <option value="Faltou">Faltou</option>
                  </select>

                  <div className="actions">
                    <button
                      className="edit-btn"
                      onClick={() => handleOpenEdit(agendamento)}
                      title="Editar agendamento"
                    >
                      <FiEdit2 /> <span>Editar</span>
                    </button>
                    {agendamento.status !== 'Concluído' && agendamento.status !== 'Cancelado' && (
                      <button
                        className="concluir-btn"
                        onClick={() => handleConcluir(agendamento.id)}
                        title="Concluir e lançar"
                      >
                        <FiCheckCircle />
                      </button>
                    )}
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(agendamento.id)}
                      title="Excluir"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingId ? 'Editar Agendamento' : 'Novo Agendamento'}
      >
        <form onSubmit={handleSubmit} className="form">
          <label>
            Cliente *
            <SearchableSelect
              required
              value={formData.cliente_id}
              onChange={(value) => setFormData({ ...formData, cliente_id: value })}
              placeholder="Selecione um cliente"
              searchPlaceholder="Digite nome ou telefone"
              options={clientes.map((cliente) => ({
                value: String(cliente.id),
                label: `${cliente.nome} (${formatPhone(cliente.telefone)})`,
                searchText: `${cliente.nome} ${cliente.telefone}`,
              }))}
            />
          </label>

          <div className="agenda-grid">
            <label>
              Data *
              <input
                type="date"
                required
                value={formData.data_agendamento}
                onChange={(e) => setFormData({ ...formData, data_agendamento: e.target.value })}
              />
            </label>

            <label>
              Hora *
              <div className="time-with-icon">
                <FiClock />
                <input
                  type="time"
                  required
                  value={formData.hora_inicio}
                  onChange={(e) => setFormData({ ...formData, hora_inicio: e.target.value })}
                />
              </div>
            </label>
          </div>

          <label>
            Serviços *
            <input
              type="text"
              value={serviceSearch}
              onChange={(event) => setServiceSearch(event.target.value)}
              placeholder="Digite para filtrar serviços"
            />
            <div className="servicos-checklist">
              {filteredServicos.map((servico) => (
                <label key={servico.id} className="servico-check-item">
                  <input
                    type="checkbox"
                    checked={formData.servico_ids.includes(String(servico.id))}
                    onChange={() => toggleServico(servico.id)}
                  />
                  <span>{formatServiceName(servico.nome)} ({servico.duracao_minutos || 60} min) - {formatCurrency(servico.valor_padrao)}</span>
                </label>
              ))}
              {filteredServicos.length === 0 && (
                <p className="agenda-empty-services">Nenhum serviço encontrado para o filtro digitado.</p>
              )}
            </div>
          </label>

          {servicosSelecionados.length > 0 && (() => {
            const { totalMinutos, bucoDesconsiderado } = calculateEstimatedDuration(servicosSelecionados);
            return (
              <div className="agenda-summary">
                <div>
                  <strong>{servicosSelecionados.length} serviço(s) selecionado(s)</strong> • Padrão dos serviços: {formatDurationText(totalMinutos)}
                </div>
                {bucoDesconsiderado && (
                  <div className="agenda-summary-note">
                    ℹ️ O tempo de atendimento do Buço (30 min) foi desconsiderado, pois é realizado durante o tempo de ação dos produtos.
                  </div>
                )}
              </div>
            );
          })()}

          <label>
            Duração do Atendimento (Tempo flexível) *
            <div className="duracao-control-container">
              <div className="duracao-input-row">
                <input
                  type="number"
                  min="15"
                  max="480"
                  step="5"
                  required
                  value={formData.duracao_minutos}
                  onChange={(e) => setFormData({
                    ...formData,
                    duracao_minutos: Math.max(15, Number(e.target.value) || 15),
                    duracao_customizada: true,
                  })}
                  className="duracao-input"
                />
                <span className="duracao-unit">minutos</span>
                <span className="duracao-calc-badge">
                  {formatDurationText(formData.duracao_minutos)}
                </span>
                {calcularHorarioFim(formData.hora_inicio, formData.duracao_minutos) && (
                  <span className="duracao-fim-badge">
                    Término: <strong>{calcularHorarioFim(formData.hora_inicio, formData.duracao_minutos)}</strong>
                  </span>
                )}
                {formData.duracao_customizada && servicosSelecionados.length > 0 && (
                  <button
                    type="button"
                    className="duracao-reset-btn"
                    onClick={() => {
                      const { totalMinutos } = calculateEstimatedDuration(servicosSelecionados);
                      setFormData({
                        ...formData,
                        duracao_minutos: totalMinutos || 60,
                        duracao_customizada: false,
                      });
                    }}
                    title="Restaurar o tempo padrão calculado pelos serviços selecionados"
                  >
                    ↺ Restaurar padrão
                  </button>
                )}
              </div>

              <div className="duracao-quick-chips">
                <span className="duracao-chips-label">Ajuste rápido:</span>
                {[30, 45, 60, 75, 90, 105, 120].map((mins) => (
                  <button
                    key={mins}
                    type="button"
                    className={`duracao-chip ${Number(formData.duracao_minutos) === mins ? 'active' : ''}`}
                    onClick={() => setFormData({
                      ...formData,
                      duracao_minutos: mins,
                      duracao_customizada: true,
                    })}
                  >
                    {formatDurationText(mins)}
                  </button>
                ))}
              </div>
            </div>
          </label>

          <label>
            Status
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="Pendente">Pendente</option>
              <option value="Agendado">Agendado</option>
              <option value="Confirmado">Confirmado</option>
              <option value="Concluído">Concluído</option>
              <option value="Cancelado">Cancelado</option>
              <option value="Faltou">Faltou</option>
            </select>
          </label>

          <label>
            Observações
            <textarea
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              rows="3"
              placeholder="Anotações do agendamento"
            />
          </label>

          <div className="form-actions">
            <Button type="submit" variant="primary">
              {editingId ? 'Salvar Alterações' : 'Salvar'}
            </Button>
            <Button type="button" variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Modal de Sincronização com Google Agenda & Apple Calendar ── */}
      <Modal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        title="📅 Sincronizar com Google Agenda & iPhone (Apple)"
      >
        <div className="agenda-sync-modal">
          <p className="agenda-sync-intro">
            Acompanhe todos os agendamentos da Fran automaticamente no seu celular ou computador, com <strong>notificações 30 minutos antes de cada atendimento</strong>!
          </p>

          {/* Botões de Ação Rápida */}
          <div className="agenda-sync-quick-actions">
            {syncInfo?.webcalUrl && (
              <a
                href={syncInfo.webcalUrl}
                className="agenda-sync-btn agenda-sync-apple"
                title="Assinar no iPhone / Apple Calendar"
              >
                <span className="agenda-sync-btn-icon">🍎</span>
                <div>
                  <strong>Adicionar ao iPhone / Apple</strong>
                  <span>Sincroniza com o Calendário do iOS, Mac e Apple Watch</span>
                </div>
              </a>
            )}

            {syncInfo?.googleCalendarUrl && (
              <a
                href={syncInfo.googleCalendarUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="agenda-sync-btn agenda-sync-google"
                title="Adicionar ao Google Agenda"
              >
                <span className="agenda-sync-btn-icon">📅</span>
                <div>
                  <strong>Adicionar ao Google Agenda (Gmail)</strong>
                  <span>Abre direto na sua conta do Google Calendar</span>
                </div>
              </a>
            )}
          </div>

          {/* Link Manual / Copiar Link */}
          <div className="agenda-sync-copy-box">
            <label>Link de Assinatura iCal (.ics):</label>
            <div className="agenda-sync-copy-row">
              <input
                type="text"
                readOnly
                value={syncInfo?.httpUrl || ''}
                className="agenda-sync-input"
              />
              <button
                type="button"
                className="agenda-sync-copy-btn"
                onClick={handleCopyFeedUrl}
              >
                {copied ? <FiCheck /> : <FiCopy />}
                <span>{copied ? 'Copiado!' : 'Copiar'}</span>
              </button>
            </div>
          </div>

          {/* Instruções Passo a Passo */}
          <div className="agenda-sync-guide">
            <h4>📖 Como configurar passo a passo:</h4>
            
            <div className="agenda-sync-step">
              <span className="step-badge">iPhone / iPad</span>
              <p>
                1. No iPhone, toque no botão <strong>"Adicionar ao iPhone"</strong> acima.<br />
                2. Toque em <strong>"Assinar"</strong> na janela que abrir.<br />
                3. Pronto! Os agendamentos aparecem no app Calendário do iPhone e avisam 30 minutos antes.
              </p>
            </div>

            <div className="agenda-sync-step">
              <span className="step-badge">Google Agenda (Gmail)</span>
              <p>
                1. No computador ou navegador do celular, abra o <strong>Google Agenda</strong>.<br />
                2. Na barra lateral esquerda, clique em <strong>Outras agendas (+)</strong> &gt; <strong>Do URL</strong>.<br />
                3. Cole o link copiado acima e clique em <strong>Adicionar agenda</strong>.
              </p>
            </div>
          </div>

          <div className="form-actions" style={{ marginTop: '20px' }}>
            <Button type="button" variant="primary" onClick={() => setShowSyncModal(false)}>
              Entendido
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Agenda;
